// @ts-strict-ignore
import type { GeofenceJson } from "@dashboardApi/apis/engagementGeofencesData/types";
import type { GeofenceDataRequest } from "@dashboardApi/apis/engagementGeofencesData/types";
import type { GeofenceDataResult } from "@dashboardApi/apis/engagementGeofencesData/types";

import type { CreateGeofenceData } from "../engagementCreateLocationSet";

export const geofenceJsonToGeofenceCreateDataResult = (json: GeofenceJson): CreateGeofenceData => {
  return {
    analytics_enabled_enter: json.analyticsEnter,
    analytics_enabled_exit: json.analyticsExit,
    radius: json.radius,
    lat: json.latitude,
    name: json.name || json.displayName,
    lng: json.longitude,
    location: [json.latitude, json.longitude],
    local_id: json.id,
  };
};

export const geofenceJsonToGeofencesDataResult = (json: GeofenceJson): GeofenceDataRequest => {
  /**
   * CREATE:
   * - to create a new geofence, we need to send to pass a local_id
   * - this tells the backend that this is a new geofence
   * UPDATE:
   * - to update an existing geofence, we need to send to pass an id
   * NOTES:
   * - the backend will return a persisted id for new geofences
   * - this replaces the local_id
   * FAQ:
   * - why do geofences have a local_id and an id?
   * - when we create a new geofence the id is used to track the geofence, useful for CRUD operations before and after the geofence is saved
   */

  return {
    ...(json.persisted ? { id: json.id } : { local_id: json.id }),
    analytics_enabled_exit: json.analyticsExit,
    analytics_enabled_enter: json.analyticsEnter,
    radius: json.radius,
    lat: json.latitude,
    cooldown_exit: json.cooldownExit,
    cooldown_enter: json.cooldownEnter,
    name: json.name,
    lng: json.longitude,
    notification_responsiveness: json.notificationResponsiveness,
  };
};

export const geofenceDataToGeofenceJson = (data: GeofenceDataResult): GeofenceJson => {
  return {
    analyticsExit: data.analytics_enabled_exit,
    analyticsEnter: data.analytics_enabled_enter,
    radius: data.radius,
    latitude: data.lat,
    cooldownExit: data.cooldown_exit,
    cooldownEnter: data.cooldown_enter,
    name: data.name,
    id: data.id,
    locationSetId: data.ls_id,
    longitude: data.lng,
    notificationResponsiveness: data.notification_responsiveness,
    displayName: data.name,
    persisted: true,
  };
};

export const applyGeofenceLocalIdMapping = (
  geofences: GeofenceJson[],
  localIdToCreatedId: Record<string, string>,
  locationSetId?: string
): GeofenceJson[] => {
  if (Object.keys(localIdToCreatedId).length === 0) {
    return geofences;
  }

  return geofences.map((geofence) => {
    const persistedId = localIdToCreatedId[geofence.id];
    if (!persistedId) {
      return geofence;
    }

    return {
      ...geofence,
      id: persistedId,
      persisted: true,
      locationSetId: locationSetId ?? geofence.locationSetId,
    };
  });
};
